/**
 * The key-figure tiles above the overview grid (masterplan credit-limits-v3, A7; E16, E30): over limit, near limit,
 * preview over limit, overdue red, overdue amber, each with the number of customers and their balance in CHF. A tile is
 * a toggle button: a click filters the list to its customers (`?band=`), a second click lifts the filter. The counts
 * come from `tileStats` over the rows the page filters leave, so a pressed tile does not zero the others.
 */
import { AlertOctagon, AlertTriangle, Clock } from 'lucide-react';
import type { TileKey, TileStat } from './overviewFilters';
import { TILES } from './overviewFilters';
import { formatChf } from './overviewColumns';

interface Props {
    stats: Record<TileKey, TileStat>;
    active: TileKey | null;
    nearLimitPct: number;
    onToggle: (key: TileKey) => void;
}

function TileIcon({ tile }: { tile: TileKey }) {
    if (tile === 'overdue-red' || tile === 'overdue-amber') return <Clock className="i sm" aria-hidden="true" />;
    if (tile === 'near') return <AlertTriangle className="i sm" aria-hidden="true" />;
    return <AlertOctagon className="i sm" aria-hidden="true" />;
}

export function OverviewTiles({ stats, active, nearLimitPct, onToggle }: Props) {
    return (
        <div className="tiles" role="group" aria-label="Key figures; a tile filters the list">
            {TILES.map(t => {
                const s = stats[t.key];
                const pressed = active === t.key;
                return (
                    <button
                        key={t.key}
                        type="button"
                        className={`tile ${t.tone}`}
                        aria-pressed={pressed}
                        title={`${t.title(nearLimitPct)}. ${pressed ? 'Click to show all customers again.' : 'Click to show only these customers.'}`}
                        onClick={() => onToggle(t.key)}
                        data-tile={t.key}
                    >
                        <span className="tile-label"><TileIcon tile={t.key} />{t.label}</span>
                        <span className="tile-count">{s.count.toLocaleString('de-CH')}</span>
                        <span className="tile-sum">Balance CHF {formatChf(s.balanceChf)}</span>
                    </button>
                );
            })}
        </div>
    );
}
